<template>
  <v-card
    v-bind="$attrs"
    class="pa-0 ma-0"
    outlined
    :style="style"
    @click="handleCardClick"
    @click.middle="openInNewTab"
    :tabindex="noTabFocus ? -1 : 0">
    <v-card-text class="pa-2 event-card-content">
      <div class="event-signal-overlay">
        <event-signal-chip :status="signalStatus" />
      </div>
      <div ref="root" style="width: 100%">
        <component :is="currentVariant" :event="event" :eventColor="eventColor" :onEventColor="onEventColor"
          :categoryIcon="categoryIcon" :categoryName="categoryName" :background="background" :costs="costs"
          :isOpen="isOpen" :taskTree="taskTree" :notifyUsers="notifyUsers" :cancelTask="cancelTask"
          :closeAllTasks="closeAllTasks" :notifiedUsers="notifiedUsers" :noTabFocus="noTabFocus" />
      </div>
    </v-card-text>
  </v-card>
</template>

<script>

import { taskTree } from "@/store/modules/task"
import { isEventOpen, getRootTask, getEventCosts, getNotifiedUsers } from "@/store/modules/event"
import { Categories } from '@/store/modules/event';
import { createEventActions } from '@/composables/useEventActions'
import EventCardMobile from './EventCardMobile.vue'
import EventCardDesktop from './EventCardDesktop.vue'
import EventCardLarge from './EventCardLarge.vue'
import EventSignalChip from './EventSignalChip.vue'
import { EVENT_SIGNAL_STATUS, resolveEventSignalStatus } from './eventSignal'

export default {
  name: "EventCardResponsive",

  beforeMount() {
    if (this.event && !this.event.site) {
      console.warn('Event missing site data in EventCardResponsive:', this.event.id)
      this.$set(this.event, 'site', {})
    }

    this.updateOpen()
    const actions = createEventActions(this)
    this.closeAllTasks = actions.closeAllTasks
    this.notifyUsers = actions.notifyUsers
    this.cancelTask = actions.cancelTask
  },
  data() {
    return {
      isOpen: false,
      rootWidth: 0,
    }
  },
  computed: {
    eventColor() {
      return Categories.get(this.event.code.category)?.color ?? ""
    },
    onEventColor() {
      return Categories.get(this.event.code.category)?.onColor ?? ""
    },
    categoryName() {
      return Categories.get(this.event.code.category)?.name ?? "Unknown category"
    },
    categoryIcon() {
      return Categories.get(this.event.code.category)?.icon ?? "mdi-alert-circle-outline"
    },
    style() {
      if (this.signalStatus === EVENT_SIGNAL_STATUS.PENDING_ACK) {
        return "border: 3px solid " + this.eventColor + ";"
      }
      if (this.isOpen) {
        return "border: 3px solid " + this.eventColor + ";"
      } else {
        return ""
      }
    },
    background() {
      if (this.isOpen || this.signalStatus === EVENT_SIGNAL_STATUS.PENDING_ACK) {
        return `font-weight-black`
      } else {
        return ""
      }
    },
    signalStatus() {
      return resolveEventSignalStatus(this.changeFlags, this.isOpen)
    },
    rootTask() {
      return getRootTask(this.event)
    },
    dispatcherTask() {
      // Required by useEventActions
      return getRootTask(this.event)
    },
    allTasks() {
      // Required by useEventActions - return all tasks from event
      return this.event?.tasks || []
    },
    costs() {
      return getEventCosts(this.event, this.$store)
    },
    notifiedUsers() {
      return getNotifiedUsers(this.event)
    },
    isNarrow() {
      // Width threshold, e.g. 600px
      return this.rootWidth < 600;
    },
    isMedium() {
      return this.rootWidth >= 600 && this.rootWidth < 960;
    },
    currentVariant() {
      if (this.isNarrow) return 'EventCardMobile';
      if (this.isMedium) return 'EventCardLarge';
      return 'EventCardDesktop';
    },
  },
    watch: {
    event: {
      handler: function (newEvent) {
        if (newEvent && !newEvent.site) {
          console.warn('Event missing site data in EventCardResponsive:', newEvent.id)
          this.$set(newEvent, 'site', {})
        }
        this.updateOpen()
      }
    },
  },
  mounted() {
    this.updateOpen();
    this.initResizeObserver();
  },
  beforeDestroy() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
  },
  methods: {
    taskTree,

    openInNewTab() {
      const route = this.$router.resolve({ name: 'Event', params: { id: this.event.id } })
      window.open(route.href, '_blank')
    },

    handleCardClick(clickEvent) {
      const target = clickEvent.target;
      const isButton = target.closest('button, .v-btn, .v-btn__content');

      if (isButton) {
        return;
      }

      if (clickEvent.ctrlKey || clickEvent.metaKey) {
        this.openInNewTab();
        return;
      }

      try { localStorage.setItem('events:scrollY', String(window.scrollY)) } catch (e) { /* ignore */ }
      this.$router.push({ name: 'Event', params: { id: this.event.id } })
    },
    updateOpen() {
      this.isOpen = isEventOpen(this.event)
    },
    refresh() {
      this.updateOpen()
    },
    addTask(task) {
      this.$emit('task-updated', task)
    },
    initResizeObserver() {
      const el = this.$refs.root;
      if (!el) return;
      this._resizeObserver = new ResizeObserver(entries => {
        for (let entry of entries) {
          this.rootWidth = entry.contentRect.width;
        }
      });
      this._resizeObserver.observe(el);
    },
  },
  components: {
    EventCardMobile,
    EventCardDesktop,
    EventCardLarge,
    EventSignalChip,
  },
  props: {
    event: {
      type: Object,
      default: null
    },
    siteHidden: {
      type: Boolean,
      default: null
    },
    noTabFocus: {
      type: Boolean,
      default: false
    },
    changeFlags: {
      type: Object,
      default: () => ({})
    }
  }
}
</script>

<style scoped>
.event-card-content {
  position: relative;
}

.event-signal-overlay {
  position: absolute;
  top: -8px;
  left: -4px;
  z-index: 2;
}
</style>
